import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditNoteSharpIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axiosInstance from '../../../Config/axiosInstance';
import AddClientContracts from './AddClientContracts';
import EditClientContracts from './EditClientContracts';
import ClientContractDeleteDialog from './ClientContractDeleteDialog';
import './ClientContracts.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { format, parseISO, isValid } from "date-fns";
import HistoryIcon from "@mui/icons-material/History";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import CreateClientAddendum from "./CreateClientAddendum";
import HistoryClientAddendum from "./HistoryClientAddendum";
import EditClientAddendum from "./EditClientAddendum";

const getStatusClass = (status) => {
  switch (status) {
    case "Active":
      return "status-active";
    case "Completed":
      return "status-completed";
    case "Suspended":
      return "status-suspended";
    case "Cancelled":
      return "status-cancelled";
    default:
      return "status-default";
  }
};

const StatusPill = ({ value }) => {
  const cls = getStatusClass(value);

  return (
    <span className={`status-pill ${cls}`}>
      {value || "Active"}
    </span>
  );
};

function ClientContracts() {
  // State-uri pentru drepturile utilizatorului.
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  //State pentru datele afișate în tabel.
  const [rows, setRows] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();

  //State-uri pentru dialogul principal de ștergere.

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  //State-uri pentru dialogul de eroare.
  // Acesta este folosit dacă apare o problemă la fetch sau la ștergere.

  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');


  // Formatează o dată ISO în format DD.MM.YYYY.
  // Dacă data nu este validă, returnează valoarea originală.
  const formatDate = (value) => {
    if (!value) return "";
    try {
      const date = parseISO(value);
      return isValid(date) ? format(date, "dd.MM.yyyy") : value;
    } catch {
      return value;
    }
  };

  /*
   Formatează valori numerice pentru afișare în stil românesc:
    - punct pentru mii
    - virgulă pentru zecimale
    Dacă există și text după număr (ex: monedă), îl păstrează.
   */
  const formatNumberRO = (value) => {
    if (value === null || value === undefined || value === "") return "-";

    // Extrage doar partea numerică
    const raw = String(value).trim();
    const match = raw.match(/-?[\d.,\s]+/);
    if (!match) return raw;

    const numericPart = match[0].trim();
    let normalized = numericPart.replace(/\s/g, "");

    // Normalizează formatul numeric
    if (normalized.includes(",") && normalized.includes(".")) {
      normalized = normalized.replace(/\./g, "").replace(",", ".");
    } else if (normalized.includes(",")) {
      normalized = normalized.replace(",", ".");
    }

    const n = Number(normalized);
    if (Number.isNaN(n)) return raw;

    // Formatează cu punct pentru mii și virgulă pentru zecimale
    const formatted = new Intl.NumberFormat("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);

    const suffix = raw.replace(match[0], "").trim();
    return suffix ? `${formatted} ${suffix}` : formatted;
  };

  /*
   Extrage codurile de proiect din liniile contractului.
   Elimină dublurile și le concatenează într-un singur string.
   */
  const getProjectCodesFromLines = (lines) => {
    if (!Array.isArray(lines) || lines.length === 0) return "-";

    const codes = [
      ...new Set(
        lines
          .map((line) => line?.project_code_label)
          .filter(Boolean)
      )
    ];

    return codes.length ? codes.join(", ") : "-";
  };

  /*
   La încărcarea componentei:
   - citește permisiunile din localStorage
   - verifică drepturile pentru pagina /client_contracts
   - setează dacă utilizatorul poate crea, edita sau șterge
   */
  useEffect(() => {
    const requiredPath = "/client_contracts";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const pages = storedPermissions?.pages || [];

    const matchPage = (page) => {
      try {
        return new URL(page.url, window.location.origin).pathname === requiredPath;
      } catch {
        return page.url === requiredPath;
      }
    };

    setCanCreate(pages.some((p) => matchPage(p) && p.can_create === true));
    setCanEdit(pages.some((p) => matchPage(p) && p.can_edit === true));
    setCanDelete(pages.some((p) => matchPage(p) && p.can_delete === true));
  }, []);

  // Navighează către pagina de adăugare contract nou.
  const handleAddClick = () => navigate('/client_contracts/add-client-contracts');

  //Navighează către pagina de editare pentru contractul selectat.
  const handleEditClick = (row) => navigate(`/client_contracts/edit-client-contracts/${row.id}`);

  const handleCreateAddendumClick = (row) => {
    navigate(`/client_contracts/create-addendum/${row.id}`);
  };

  const handleAddendumHistoryClick = (row) => {
    navigate(`/client_contracts/contracts-history/${row.id}`);
  };

  //Deschide dialogul de ștergere 
  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setOpenDeleteDialog(true);
  };

  /*
   Aduce lista de contracte din backend.
   - transformă datele pentru DataGrid
   - completează project_code din lines dacă nu există direct
   - sortează descrescător după id
   */
  const fetchContracts = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/client-contracts/");

      const sortedData = (res.data || [])
        .map((item) => ({
          id: item.id,
          nr: item.nr,
          client_document: item.client_document || "-",
          project_code: item.project_code_label || getProjectCodesFromLines(item.lines),
          status: item.status || "Active",
          sign_date: item.sign_date,
          end_date: item.end_date,
          responsible_person: item.responsible_person_label || "-",
          contract_type: item.contract_type,
          client: item.client_name || "-",
          object: item.object,
          total_contract_display: item.total_with_addendums_display || "-",
          total_system_contract: item.total_system_with_addendums || "-",
        }))
        .sort((a, b) => b.id - a.id);

      setRows(sortedData);
    } catch (error) {
      console.error("Eroare la obținerea contractelor:", error.response?.data || error.message);
      setDeleteErrorMessage(error.response?.data?.message || error.message);
      setErrorDialogOpen(true);
    }
  }, []);

  /*
   Reîncarcă lista de contracte:
   - la prima montare
   - sau când se schimbă ruta curentă
   */
  useEffect(() => {
    fetchContracts();
  }, [fetchContracts, location.pathname]);

  /*
    Definirea coloanelor pentru DataGrid.
   Include formatare pentru date, totaluri și câmpurile text.
   */
  const columns = [
    {
      field: 'client',
      headerName: 'Client',
      flex: 1.5,
      minWidth: 250,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'nr',
      headerName: 'Contract Nr.',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'client_document',
      headerName: 'Client Document',
      flex: 1,
      minWidth: 170,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'project_code',
      headerName: 'Project Code',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'object',
      headerName: 'Object',
      flex: 2,
      minWidth: 260,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'contract_type',
      headerName: 'Contract Type',
      flex: 1,
      minWidth: 170,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 170,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <StatusPill value={params.value} />
        </div>
      )
    },
    {
      field: 'total_contract_display',
      headerName: 'Total',
      flex: 1.5,
      minWidth: 170,
      sortable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%', fontWeight: '500' }}>
          {formatNumberRO(params.value)}
        </div>
      )
    },
    {
      field: 'total_system_contract',
      headerName: 'Total System',
      flex: 1.5,
      minWidth: 170,
      sortable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%', fontWeight: '500' }}>
          {formatNumberRO(params.value)}
        </div>
      )
    },
    {
      field: 'sign_date',
      headerName: 'Sign Date',
      flex: 0.8,
      minWidth: 110,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {formatDate(params.value)}
        </div>
      )
    },
    {
      field: 'end_date',
      headerName: 'End Date',
      flex: 1.2,
      minWidth: 110,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {formatDate(params.value)}
        </div>
      )
    },
    {
      field: 'responsible_person',
      headerName: 'Responsible Person',
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
  ];

  if (canEdit || canDelete) {
    columns.push({
      field: 'action',
      headerName: 'Action',
      width: 180,
      renderCell: (params) => (
        <>
          {canEdit && (
            <IconButton
              onClick={() => handleEditClick(params.row)}
              sx={{ color: '#1976d2' }}
              title="Edit contract"
            >
              <EditNoteSharpIcon />
            </IconButton>
          )}

          {canEdit && (
            <IconButton
              onClick={() => handleAddendumHistoryClick(params.row)}
              sx={{ color: '#6a1b9a' }}
              title="View addendums"
            >
              <HistoryIcon />
            </IconButton>
          )}

          <span
            title={
              params.row.contract_type === "Purchase Order"
                ? "Addendums cannot be created for Purchase Orders."
                : "Create addendum"
            }
          >
            <IconButton
              onClick={() => handleCreateAddendumClick(params.row)}
              disabled={params.row.contract_type === "Purchase Order"}
              sx={{
                color:
                  params.row.contract_type === "Purchase Order"
                    ? "#bdbdbd"
                    : "#2e7d32",
                cursor:
                  params.row.contract_type === "Purchase Order"
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              <LibraryAddIcon />
            </IconButton>
          </span>

          {canDelete && (
            <IconButton
              onClick={() => handleDeleteClick(params.row)}
              sx={{ color: '#d32f2f' }}
              title="Delete contract"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </>
      ),
      sortable: false,
      disableColumnMenu: true,
    });
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className="client-contracts-page">
            <div className="client-contracts-container">
              <h2 className='title'>CLIENT CONTRACT</h2>

              {/* Butonul de adăugare apare doar dacă utilizatorul are drept de creare */}
              {canCreate && (
                <div className="add-button-container">
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    disabled={!canCreate}
                    sx={{ float: 'right', marginBottom: '40px' }}
                  >
                    New Contract
                  </Button>
                </div>
              )}
            </div>

            <div className="table-container">
              {/* Tabelul principal cu lista contractelor */}
              <DataGrid
                id="client-contract-table"
                rows={rows}
                columns={columns}
                getRowHeight={() => 'auto'}
                disableRowSelectionOnClick={true}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'id', sort: 'desc' }]
                  },
                  pagination: {
                    paginationModel: {
                      pageSize: 12,
                    },
                  },
                }}
                pageSizeOptions={[10, 12, 20, 25]}
                sx={{
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                    display: "flex",
                    alignItems: "center"
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    whiteSpace: "normal"
                  },
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                  "& .Mui-selected:hover": {
                    backgroundColor: "transparent !important"
                  },
                }}
              />
            </div>

            {/* Dialogul principal pentru confirmarea ștergerii */}
            <ClientContractDeleteDialog
              open={openDeleteDialog}
              record={itemToDelete}
              onClose={() => {
                setOpenDeleteDialog(false);
                setItemToDelete(null);
              }}
              onDeleted={() => {
                setOpenDeleteDialog(false);
                setItemToDelete(null);
                fetchContracts(); // Reîncarcă datele după ștergere
              }}
            />

            {/* Dialogul de eroare pentru cazurile în care operația eșuează */}
            <Dialog
              open={errorDialogOpen}
              onClose={() => setErrorDialogOpen(false)}
              aria-labelledby="error-dialog-title"
              aria-describedby="error-dialog-description"
            >
              <DialogTitle id="error-dialog-title" style={{ color: 'red' }}>
                Delete Failed
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="error-dialog-description">
                  {deleteErrorMessage}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setErrorDialogOpen(false)}
                  sx={{
                    backgroundColor: '#e0e0e0',
                    color: '#424242',
                    '&:hover': {
                      backgroundColor: '#bdbdbd',
                    }
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        }
      />

      {/* Ruta pentru adăugare contract */}
      <Route path="add-client-contracts" element={<AddClientContracts />} />

      {/* Ruta pentru editare contract */}
      <Route path="edit-client-contracts/:id" element={<EditClientContracts />} />

      <Route
        path="create-addendum/:contractId"
        element={<CreateClientAddendum />}
      />

      <Route
        path="contracts-history/:contractId"
        element={<HistoryClientAddendum />}
      />

      <Route
        path="edit-addendum/:addendumId"
        element={<EditClientAddendum />}
      />

    </Routes>
  );
}

export default ClientContracts;